import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  MessageCircle, 
  MoreHorizontal, 
  Play, 
  Share2,
  Trash2,
  ExternalLink,
  ThumbsUp,
  ThumbsDown,
  Pencil,
  Tv2,
  Tv,
  Globe,
  FileText,
  Download,
  Loader2,
  BarChart3,
  AlertTriangle,
} from 'lucide-react';
import { Video, ShareVisibility } from '../types/video';
import ShareLinkDialog from './ShareLinkDialog';
import CustomVideoPlayer from './CustomVideoPlayer';
import { apiClient } from '../lib/apiClient';
import { useTranslation } from 'react-i18next';
import { toast } from '../lib/toast';
import { ConfirmDialog } from './ConfirmDialog';
import Comments from './Comments';
import { formatClock } from '../lib/editorTime';
import { useReadOnly } from '../hooks/useArchiveAccess';
import { BIN_RETENTION_DAYS, toastMovedToBin } from '../hooks/useRecycleBin';
import BodyPortal from './BodyPortal';
import RichText from './RichText';
import EditVideoDetailsDialog from './EditVideoDetailsDialog';
import VideoInsightsDialog from './VideoInsightsDialog';
import Mp4DownloadDialog from './Mp4DownloadDialog';
import { broadcastVideosRefresh } from '../hooks/useVideos';
import { useMp4Download, videoFormat } from '../hooks/useMp4Download';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://nodebackendforacvrecording-1.onrender.com';

interface VideoCardProps {
  video: Video;
  /** May return a promise resolving to false when the delete failed. */
  onDelete?: (videoId: string) => void | Promise<boolean | void>;
  onReaction?: (videoId: string, reaction: 'like' | 'dislike') => void;
  onEdit?: (video: Video) => void;
  /** All channels owned by the current user — enables Publish/Remove from channel actions */
  myChannels?: import('../types/channel').Channel[];
  /** @deprecated Use myChannels instead */
  myChannelId?: string;
  /** Called after a successful channel assignment so the parent can refresh its list */
  onChannelAssigned?: (videoId: string, channelId: string | null) => void;
}

// ── Channel Picker Modal ──────────────────────────────────────────────────────
interface ChannelPickerModalProps {
  channels: import('../types/channel').Channel[];
  currentChannelId?: string;
  videoTitle: string;
  assigning: boolean;
  onSelect: (channelId: string) => void;
  onClose: () => void;
}

const ChannelPickerModal: React.FC<ChannelPickerModalProps> = ({
  channels, currentChannelId, videoTitle, assigning, onSelect, onClose,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = React.useState('');
  const filtered = query.trim()
    ? channels.filter((ch) => ch.displayName?.toLowerCase().includes(query.toLowerCase()))
    : channels;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--card, #ffffff)',
        color: 'var(--cardForeground, #1a1a1a)',
        borderRadius: 14,
        width: 'min(92vw, 400px)',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 40px rgba(0,0,0,0.28)',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 18px 12px',
          borderBottom: '1px solid var(--border, #e5e7eb)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tv2 size={16} style={{ color: 'var(--primary-text, #4f46e5)' }} aria-hidden />
            <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--cardTitleColor, #111827)' }}>
              {t('channel_picker.title', 'Publish to channel')}
            </h2>
          </div>
          <button type="button" onClick={onClose} aria-label={t('close', 'Close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--textSecondary, #6b7280)', fontSize: 18, lineHeight: 1, padding: 2 }}>
            ✕
          </button>
        </div>

        {/* Video reference */}
        <p style={{ margin: 0, padding: '8px 18px 4px', fontSize: 11, color: 'var(--textSecondary, #6b7280)',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', flexShrink: 0 }}>
          {videoTitle}
        </p>

        {/* Search */}
        {channels.length > 4 && (
          <div style={{ padding: '6px 12px 8px', flexShrink: 0 }}>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('channel_picker.search', 'Search channel…')}
              aria-label={t('channel_picker.search_aria', 'Search channel')}
              autoFocus
              style={{
                width: '100%', boxSizing: 'border-box',
                padding: '7px 12px', borderRadius: 8, fontSize: 13,
                border: '1px solid var(--controlsBorder, #d1d5db)',
                background: 'var(--controlsBg, #f9fafb)',
                color: 'var(--controlsForeground, #111827)',
                outline: 'none',
              }}
            />
          </div>
        )}

        {/* Channel list — scrollable */}
        <div style={{ overflowY: 'auto', padding: '4px 12px 14px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--textSecondary, #6b7280)', fontSize: 13, margin: '20px 0' }}>
              {t('channel_picker.no_results', 'No results')}
            </p>
          ) : filtered.map((ch) => {
            const isActive = ch.id === currentChannelId;
            return (
              <button
                key={ch.id}
                type="button"
                disabled={assigning}
                onClick={() => onSelect(ch.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 12px', borderRadius: 9, cursor: assigning ? 'default' : 'pointer',
                  background: isActive ? 'var(--surfaceSelected, rgba(79,70,229,0.08))' : 'var(--cardHoverBg, #f9fafb)',
                  border: isActive ? '1px solid var(--primary, #4f46e5)' : '1px solid var(--border, #e5e7eb)',
                  color: 'var(--cardForeground, #1a1a1a)',
                  textAlign: 'left', width: '100%',
                  transition: 'background 0.12s, border-color 0.12s',
                  opacity: assigning ? 0.6 : 1,
                }}
              >
                <Tv2 size={15} style={{ color: isActive ? 'var(--primary, #4f46e5)' : 'var(--textSecondary, #9ca3af)', flexShrink: 0 }} aria-hidden />
                <span style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, flex: 1,
                  overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                  {ch.displayName || ch.id}
                </span>
                {isActive && (
                  <span style={{ fontSize: 11, color: 'var(--primary-text, #4f46e5)', fontWeight: 700, flexShrink: 0 }}>
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const VideoCard: React.FC<VideoCardProps> = ({ video: videoProp, onDelete, onEdit, myChannels, myChannelId, onChannelAssigned }) => {
  // Title/description just edited here show at once; the next list refresh brings the same values.
  const [editedDetails, setEditedDetails] = useState<{ title: string; description: string; thumbnailUrl?: string } | null>(null);
  useEffect(() => { setEditedDetails(null); }, [videoProp.title, videoProp.description]);
  const video = editedDetails ? { ...videoProp, ...editedDetails } : videoProp;
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  // Normalize: support both old myChannelId and new myChannels prop
  const resolvedMyChannels = myChannels ?? (myChannelId ? [{ id: myChannelId, displayName: '' } as import('../types/channel').Channel] : []);
  const { t } = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const countsLoadedRef = useRef(false);
  const [showMenu, setShowMenu] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [libraryVideoEnded, setLibraryVideoEnded] = useState(false);
  /** The player's <video>: comments read its time and seek it. */
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [commentCount, setCommentCount] = useState(0);
  const [assigningChannel, setAssigningChannel] = useState(false);
  const [localChannelId, setLocalChannelId] = useState<string | undefined>(video.channelId ?? undefined);
  // Follow the server value when the list is refreshed (e.g. the video was removed from the
  // channel elsewhere) instead of keeping the value from the first render.
  useEffect(() => { setLocalChannelId(video.channelId ?? undefined); }, [video.channelId]);
  const [showChannelPicker, setShowChannelPicker] = useState(false);
  // Publishing to / removing from a channel waits for confirmation in an in-app dialog.
  const [channelConfirm, setChannelConfirm] = useState<
    { kind: 'publish'; channelId: string } | { kind: 'remove' } | null
  >(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Deleted: the card fades until the refreshed library drops it.
  const [removed, setRemoved] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // My Library playback is in-iframe content, not a blocking host modal.
  // Do not send acv-iframe-modal-open: ACVLayout would veil navbar/chatbar
  // (Docs/ACV-Iframe-Communication-Contract-EN.md points 7-8).
  const [likes, setLikes] = useState(0);
  const [dislikes, setDislikes] = useState(0);
  const [emojiSummary, setEmojiSummary] = useState<{ emoji: string; count: number }[]>([]);

  const formatDate = (dateString: string | undefined) => {
    if (!dateString || dateString === 'undefined' || dateString === 'null') {
      return t('time_units.just_now');
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        console.error('Invalid date string:', dateString);
        return t('time_units.just_now');
      }
      
      const now = new Date();
      const diffMs = Math.abs(now.getTime() - date.getTime());
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      const diffHours   = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays    = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffMinutes < 1)  return t('time_units.just_now');
      if (diffHours   < 1)  return diffMinutes === 1
        ? t('time_units.minute_ago')
        : t('time_units.minutes_ago', { count: diffMinutes });
      if (diffDays    < 1)  return diffHours === 1
        ? t('time_units.hour_ago')
        : t('time_units.hours_ago', { count: diffHours });
      if (diffDays    === 1) return t('time_units.day_ago');
      if (diffDays    < 7)   return t('time_units.days_ago', { count: diffDays });
      if (diffDays    < 30) {
        const weeks = Math.floor(diffDays / 7);
        return weeks === 1
          ? t('time_units.week_ago')
          : t('time_units.weeks_ago', { count: weeks });
      }
      const months = Math.floor(diffDays / 30);
      if (diffDays < 365) return t(months === 1 ? 'time_units.month_ago' : 'time_units.months_ago', { count: months });
      const years = Math.floor(diffDays / 365);
      return t(years === 1 ? 'time_units.year_ago' : 'time_units.years_ago', { count: years });
    } catch (error) {
      console.error('Error formatting date:', error);
      return t('time_units.just_now');
    }
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (bytes === undefined || bytes === null || isNaN(bytes) || bytes === 0) {
      console.log('[VideoCard] Invalid file size:', bytes);
      return `0 ${t('file_size_units.bytes')}`;
    }
    
    try {
      const k = 1024;
      const sizes = [t('file_size_units.bytes'), t('file_size_units.kb'), t('file_size_units.mb'), t('file_size_units.gb')];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
    } catch (error) {
      console.error('[VideoCard] Error formatting file size:', error);
      return `0 ${t('file_size_units.bytes')}`;
    }
  };

  // Load comment/reaction counts only when the card enters the viewport.
  // This prevents 36+ simultaneous backend requests on mount that cause
  // connection timeouts reported by the browser as CORS errors.
  const fetchCounts = useCallback(async () => {
    try {
      const [{ data: comments }, { data: reactionData }, { data: emojiData }] = await Promise.allSettled([
        apiClient.listComments(video.id),
        apiClient.getReactions(video.id),
        apiClient.getEmojiReactionSummary(video.id),
      ]).then((results) => results.map((r) => (r.status === 'fulfilled' ? r.value : { data: null })));

      if (comments) setCommentCount((comments as import('../types/comment').Comment[]).length);
      if (reactionData) {
        const rd = reactionData as { like?: number; dislike?: number };
        setLikes(Number(rd.like) || 0);
        setDislikes(Number(rd.dislike) || 0);
      }
      if (emojiData) {
        setEmojiSummary(emojiData as { emoji: string; count: number }[]);
      }
    } catch (error) {
      // Silent — counts are cosmetic; a failed fetch should not surface to the user
    }
  }, [video.id]);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !countsLoadedRef.current) {
          countsLoadedRef.current = true;
          fetchCounts();
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [fetchCounts]);

  // Normalize video data to handle both snake_case and camelCase properties
  const toAbsoluteMediaUrl = (value?: string): string => {
    if (!value || typeof value !== 'string') return '';
    if (/^https?:\/\//i.test(value) || value.startsWith('blob:') || value.startsWith('data:')) {
      return value;
    }
    if (value.startsWith('//')) {
      return `${window.location.protocol}${value}`;
    }
    const base = API_BASE_URL.replace(/\/+$/, '');
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${base}${path}`;
  };

  const normalizedVideo = {
    ...video,
    isShared: video.isShared || video.is_shared || false,
    shareToken: video.shareToken || video.share_token || '',
    fileSize: video.fileSize || video.file_size || 0,
    createdAt: video.createdAt || video.created_at || new Date().toISOString(),
    // Compatibilidad snake_case/camelCase para evitar perder miniatura en respuestas mixtas.
    thumbnailUrl: toAbsoluteMediaUrl(video.thumbnailUrl || video.thumbnail_url || video.thumbnail || ''),
    fileUrl: toAbsoluteMediaUrl(video.file_url || video.url || ''),
    streamUrl: toAbsoluteMediaUrl(video.streamUrl || video.stream_url || ''),
  };

  // The owner can remove any comment. Non-owners viewing a channel don't receive the owner's
  // email, so this is false for them.
  const myEmail = apiClient.getUser().email?.toLowerCase();
  const isVideoOwner =
    !!myEmail && [video.userEmail, video.ownerEmail].some((e) => e?.toLowerCase() === myEmail);
  const mp4 = useMp4Download(video, isVideoOwner);
  // Admin Center Archive Mode, read-only: watching (and downloading a copy) only.
  const readOnly = useReadOnly();
  const format = videoFormat(video);
  const [showMp4Dialog, setShowMp4Dialog] = useState(false);
  const mp4Percent = Math.round(mp4.progress * 100);
  const mp4Status = {
    idle: '',
    preparing: t('mp4.preparing', 'Preparing MP4…'),
    // Save as MP4 (from the editor) replaces the video's file; Download as MP4 makes a copy.
    converting: mp4.job?.replace
      ? (mp4Percent > 0 ? t('mp4.saving_pct', 'Saving as MP4 · {{pct}}%', { pct: mp4Percent }) : t('mp4.saving', 'Saving as MP4…'))
      : (mp4Percent > 0 ? t('mp4.converting_pct', 'Converting to MP4 · {{pct}}%', { pct: mp4Percent }) : t('mp4.converting', 'Converting to MP4…')),
    downloading: t('mp4.download_starting', 'Download starting…'),
  }[mp4.phase];

  const linkExpired = !!normalizedVideo.shareExpiresAt && Date.parse(normalizedVideo.shareExpiresAt) <= Date.now();
  const isWhiteboard = !!video.source?.startsWith('whiteboard-collections');
  // Small labels on the thumbnail: dark glass, readable on any picture and in any theme.
  const thumbBadge: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 7px', borderRadius: 6,
    fontSize: 11, fontWeight: 600, lineHeight: 1.5, color: '#fff', background: 'rgba(15, 23, 42, 0.72)',
  };
  const shareStatusLabel = !normalizedVideo.isShared
    ? t('shared_status.not_shared')
    : linkExpired ? t('shared_status.expired', 'Link expired') : t('shared_status.shared');

  const handleOpenShareModal = () => {
    setShowMenu(false);
    setShowShareModal(true);
  };

  const handleDelete = () => {
    if (!onDelete) return;
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    const ok = await onDelete?.(normalizedVideo.id);
    if (ok === false) {
      toast.error(t('video_delete.failed', 'Could not move the video to the Recycle bin. Try again.'));
      return;
    }
    setShowDeleteConfirm(false);
    setRemoved(true);
    toastMovedToBin([normalizedVideo.id]);
  };

  const hasOpenOverlay =
    showVideoModal || showDeleteConfirm || showChannelPicker || channelConfirm !== null;

  const closeVideoModal = useCallback(() => {
    setShowVideoModal(false);
    setLibraryVideoEnded(false);
  }, []);

  // While the player is open: Esc closes it, and the page behind doesn't scroll.
  useEffect(() => {
    if (!showVideoModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || document.fullscreenElement) return;
      // A dialog opened from the player (confirm, insert link) handles its own Esc.
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      // Don't throw away a comment being typed.
      const el = document.activeElement as HTMLElement | null;
      if (el && (el.isContentEditable || el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      closeVideoModal();
    };
    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      root.style.overflow = previousOverflow;
    };
  }, [showVideoModal, closeVideoModal]);

  const handleVideoClick = () => {
    // Make sure we're using the normalized video data with streamUrl
    setShowVideoModal(true);
  };

  const openSharedPage = () => {
    if (normalizedVideo.shareToken) {
      window.open(`/share/${normalizedVideo.shareToken}`, '_blank');
      setShowMenu(false);
    }
  };

  const handleAssignChannel = async (targetChannelId: string | null) => {
    if (assigningChannel) return;
    setAssigningChannel(true);
    setShowMenu(false);
    const { data, error } = await apiClient.assignVideoToChannel(normalizedVideo.id, targetChannelId);
    if (data) {
      setLocalChannelId(targetChannelId ?? undefined);
      onChannelAssigned?.(normalizedVideo.id, targetChannelId);
      toast.success(targetChannelId
        ? t('channel.video_published', 'Video published to the channel')
        : t('channel.video_removed', 'Video removed from the channel'));
    } else {
      console.error('[VideoCard] assignVideoToChannel error:', error?.message);
      toast.error(t('channel.video_publish_error', 'Could not update the channel for this video.'));
    }
    setAssigningChannel(false);
  };

  // The ⋯ menu is rendered in a portal with fixed positioning, so the card's overflow
  // (and the description below the thumbnail) can never clip it. It opens below the
  // button, flips above when there is more room there, stays inside the viewport and
  // scrolls internally if it still doesn't fit.
  const positionMenu = useCallback(() => {
    const btn = menuButtonRef.current;
    const menu = menuRef.current;
    if (!btn || !menu) return;
    const GAP = 4;
    const MARGIN = 8;
    const r = btn.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Button scrolled out of view: close rather than leave a floating menu behind.
    if (r.bottom < 0 || r.top > vh) {
      setShowMenu(false);
      return;
    }
    const menuWidth = menu.offsetWidth;
    const natural = menu.scrollHeight;
    const spaceBelow = vh - r.bottom - GAP - MARGIN;
    const spaceAbove = r.top - GAP - MARGIN;
    const openUp = natural > spaceBelow && spaceAbove > spaceBelow;
    const maxHeight = Math.max(openUp ? spaceAbove : spaceBelow, 0);
    const top = openUp ? r.top - GAP - Math.min(natural, maxHeight) : r.bottom + GAP;
    const left = Math.min(Math.max(r.right - menuWidth, MARGIN), Math.max(vw - menuWidth - MARGIN, MARGIN));
    setMenuPos((prev) =>
      prev && prev.top === top && prev.left === left && prev.maxHeight === maxHeight
        ? prev
        : { top, left, maxHeight }
    );
  }, []);

  // Measure before paint so the menu never flashes in the wrong place.
  useLayoutEffect(() => {
    if (!showMenu) {
      setMenuPos(null);
      return;
    }
    positionMenu();
  }, [showMenu, positionMenu, resolvedMyChannels.length, localChannelId]);

  // Close on click/tap outside, Escape or the iframe losing focus; follow scroll/resize.
  useEffect(() => {
    if (!showMenu) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || menuButtonRef.current?.contains(target)) return;
      setShowMenu(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowMenu(false);
        menuButtonRef.current?.focus();
      }
    };
    const onBlur = () => setShowMenu(false);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('blur', onBlur);
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [showMenu, positionMenu]);

  return (
    <>
      <div
        ref={cardRef}
        className="rounded-lg overflow-hidden transition-shadow duration-200 group relative"
        aria-busy={removed || undefined}
        style={{
          ...(removed ? { opacity: 0.4, pointerEvents: 'none' as const, transition: 'opacity 0.2s ease' } : {}),
          backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
          border: '1px solid var(--card-border-color, var(--border-color, var(--theme-border, #d6d6d6)))',
          color: 'var(--card-foreground, var(--text-primary, var(--theme-foreground, #1a1a1a)))',
          boxShadow: 'var(--card-shadow, 0 4px 10px rgba(0,0,0,0.06))',
        }}
      >
        {/* Thumbnail */}
        <div
          className="relative aspect-video overflow-hidden cursor-pointer"
          style={{ backgroundColor: 'var(--surface-selected, var(--theme-primary, #4f46e5))' }}
          onClick={handleVideoClick}
        >
          {normalizedVideo.thumbnailUrl ? (
            <img
              src={normalizedVideo.thumbnailUrl}
              alt={normalizedVideo.title}
              className="w-full h-full object-cover"
            />
          ) : (normalizedVideo.streamUrl || normalizedVideo.fileUrl) ? (
            <video
              src={normalizedVideo.streamUrl || normalizedVideo.fileUrl}
              className="w-full h-full object-cover"
              preload="metadata"
              muted
              playsInline
            />
          ) : (
            <div
              className="w-full h-full flex items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, var(--button-primary-bg, var(--theme-primary, #4f46e5)), var(--button-secondary-bg, var(--surface-selected, #7c3aed)))',
              }}
            >
              <Play
                className="w-12 h-12 opacity-80"
                style={{ color: 'var(--button-primary-foreground, #ffffff)' }}
              />
            </div>
          )}
          <div
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center"
            style={{ backgroundColor: 'var(--overlay-bg, rgba(0, 0, 0, 0.25))' }}
          >
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center transform transition-transform duration-200 group-hover:scale-110"
              style={{
                backgroundColor: 'var(--dropdown-bg, var(--card-bg, #ffffff))',
                border: '1px solid var(--border-color, #d6d6d6)',
              }}
            >
              <Play
                className="w-6 h-6"
                style={{ color: 'var(--button-primary-bg, var(--theme-primary, #4f46e5))' }}
                fill="currentColor"
              />
            </div>
          </div>
          {/* Thumbnail badges: where it comes from and whether its link works (bottom-left; the selection box
              uses the top-left corner), length (bottom-right). */}
          {(isWhiteboard || normalizedVideo.isShared) && !mp4.busy && (
            <div className="absolute bottom-2 left-2 flex items-center gap-1.5" style={{ zIndex: 5, pointerEvents: 'none' }}>
              {isWhiteboard && (
                <span
                  style={{ ...thumbBadge, background: 'var(--primary)', color: 'var(--button-primary-foreground, #fff)', letterSpacing: 0.5 }}
                  title={t('wb_import.wb_badge_title', 'Imported from Whiteboard Collections')}
                >
                  {t('wb_import.wb_badge')}
                </span>
              )}
              {normalizedVideo.isShared && (
                <span style={thumbBadge} title={shareStatusLabel}>
                  {linkExpired
                    ? <AlertTriangle className="w-3 h-3" style={{ color: '#fbbf24' }} aria-hidden />
                    : <Globe className="w-3 h-3" aria-hidden />}
                  {shareStatusLabel}
                </span>
              )}
            </div>
          )}
          {Number(normalizedVideo.duration) > 0 && !mp4.busy && (
            <span className="absolute bottom-2 right-2" style={{ ...thumbBadge, zIndex: 5, pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}>
              {formatClock(Number(normalizedVideo.duration))}
            </span>
          )}
          {/* Download as MP4: preparing → converting (server job) → download starting */}
          {mp4.busy && (
            <div
              role="status"
              className="absolute bottom-2 left-2 flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium"
              style={{ zIndex: 5, background: 'rgba(15, 23, 42, 0.82)', color: '#fff' }}
            >
              {mp4.phase === 'downloading'
                ? <Download className="w-3.5 h-3.5" aria-hidden />
                : <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden />}
              {mp4Status}
            </div>
          )}
        </div>

        {/* Menu Button */}
        <div className="absolute top-2 right-2 z-10">
          <button
            ref={menuButtonRef}
            type="button"
            aria-haspopup="menu"
            aria-expanded={showMenu}
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu((open) => !open);
            }}
            className="p-1.5 rounded-full transition-colors"
            style={{
              backgroundColor: 'var(--dropdown-bg, var(--card-bg, #ffffff))',
              border: 'var(--dropdown-border, 1px solid var(--border-color, #d6d6d6))',
              boxShadow: 'var(--floating-shadow, 0 4px 10px rgba(0,0,0,0.16))',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--dropdown-bg, var(--card-bg, #ffffff))';
            }}
          >
            <MoreHorizontal
              className="w-4 h-4"
              style={{ color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))' }}
            />
          </button>
          
          {/* Dropdown Menu */}
          {showMenu && createPortal(
            <div
              ref={menuRef}
              role="menu"
              onClick={(e) => e.stopPropagation()}
              className="w-52 rounded-md py-1"
              style={{
                position: 'fixed',
                top: menuPos?.top ?? 0,
                left: menuPos?.left ?? 0,
                maxHeight: menuPos?.maxHeight,
                overflowY: 'auto',
                zIndex: 1000,
                visibility: menuPos ? 'visible' : 'hidden',
                backgroundColor: 'var(--dropdown-bg, var(--card-bg, #ffffff))',
                color: 'var(--dropdown-foreground, var(--text-primary, #1a1a1a))',
                border: 'var(--dropdown-border, 1px solid var(--border-color, #d6d6d6))',
                boxShadow: 'var(--floating-shadow, 0 10px 24px rgba(0,0,0,0.18))',
              }}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleVideoClick();
                  setShowMenu(false);
                }}
                className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                style={{
                  color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                  backgroundColor: 'transparent',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <Play className="w-4 h-4" />
                <span>{t('video_actions.play_video')}</span>
              </button>
              {isVideoOwner && !readOnly && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowDetailsDialog(true);
                  }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <FileText className="w-4 h-4" />
                  <span>{t('video_details.edit_menu', 'Edit details')}</span>
                </button>
              )}
              {isVideoOwner && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowInsights(true);
                  }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <BarChart3 className="w-4 h-4" />
                  <span>{t('insights.menu', 'Insights')}</span>
                </button>
              )}
              {onEdit && !readOnly && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    onEdit(video);
                  }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Pencil className="w-4 h-4" />
                  <span>{t('video_actions.edit_in_studio')}</span>
                </button>
              )}
              {isVideoOwner && (
                <button
                  disabled={mp4.busy}
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(false);
                    setShowMp4Dialog(true);
                  }}
                  className="w-full disabled:opacity-60 px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {mp4.busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  <span>
                    {mp4.busy ? mp4Status : t('mp4.download', 'Download as MP4')}
                  </span>
                </button>
              )}
              {!readOnly && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleOpenShareModal(); }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <Share2 className="w-4 h-4" />
                  <span>{t('video_actions.share')}</span>
                </button>
              )}
              {normalizedVideo.isShared && normalizedVideo.shareToken && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    openSharedPage();
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                  style={{
                    color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--dropdown-item-hover-bg, var(--surface-hover, rgba(0,0,0,0.06)))';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>{t('video_actions.open_shared_page')}</span>
                </button>
              )}
              {/* Channel actions — single entry that opens the picker modal */}
              {!readOnly && resolvedMyChannels.length > 0 && (() => {
                const publishedChannel = resolvedMyChannels.find((ch) => ch.id === localChannelId);
                if (publishedChannel) {
                  return (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowMenu(false); setChannelConfirm({ kind: 'remove' }); }}
                      disabled={assigningChannel}
                      className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 disabled:opacity-50 transition-colors"
                      style={{ color: 'var(--primary-text, #1976d2)', backgroundColor: 'transparent' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--surface-hover, rgba(0,0,0,0.06))'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <Tv className="w-4 h-4" />
                      <span>
                        {assigningChannel
                          ? t('channel.assigning')
                          : `${t('channel.remove_from_channel')} · ${publishedChannel.displayName}`}
                      </span>
                    </button>
                  );
                }
                return (
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); setShowChannelPicker(true); }}
                    className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                    style={{ color: 'var(--text-primary, #1a1a1a)', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--surface-hover, rgba(0,0,0,0.06))'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Tv2 className="w-4 h-4" />
                    <span>{t('channel.publish_to_channel')}</span>
                  </button>
                );
              })()}
              {onDelete && !readOnly && (
                <>
                  <div style={{ margin: '4px 12px', borderTop: '1px solid var(--border, #e5e7eb)' }} />
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                    className="w-full px-4 py-2 text-sm text-left flex items-center gap-2 transition-colors"
                    style={{ color: '#dc2626', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(220,38,38,0.07)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>{t('video_actions.delete')}</span>
                  </button>
                </>
              )}
            </div>,
            document.body
          )}
        </div>

        {/* Content: title, one meta line, counts only when there are some */}
        <div className="px-3.5 pt-3 pb-3.5">
          <h3
            className="font-semibold text-sm leading-snug line-clamp-2"
            title={video.title}
            style={{ color: 'var(--card-title-color, var(--text-primary, #1a1a1a))' }}
          >
            {video.title}
          </h3>
          <p className="text-xs mt-1" style={{ color: 'var(--text-secondary, #6b7280)' }}>
            {[
              formatDate(video.created_at || video.createdAt),
              t('library_card.views', { count: Number(normalizedVideo.views) || 0 }),
              formatFileSize(video.file_size || video.fileSize),
            ].join(' · ')}
          </p>

          {(commentCount > 0 || likes > 0 || dislikes > 0 || emojiSummary.length > 0) && (
          <div className="flex items-center gap-3 mt-2 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>
            {commentCount > 0 && (
              <span className="flex items-center gap-1" title={t('comments.heading', 'Comments')}>
                <MessageCircle className="w-3.5 h-3.5" aria-hidden />{commentCount}
              </span>
            )}
            {likes > 0 && (
              <span className="flex items-center gap-1" title={t('library_card.likes', 'Likes')}>
                <ThumbsUp className="w-3.5 h-3.5" aria-hidden />{likes}
              </span>
            )}
            {dislikes > 0 && (
              <span className="flex items-center gap-1" title={t('library_card.dislikes', 'Dislikes')}>
                <ThumbsDown className="w-3.5 h-3.5" aria-hidden />{dislikes}
              </span>
            )}
            {/* Emoji reactions received — shown as compact pills */}
            {emojiSummary.length > 0 && (
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {emojiSummary.map(({ emoji, count }) => (
                  <span
                    key={emoji}
                    title={`${count} reacción${count !== 1 ? 'es' : ''}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 2,
                      fontSize: 11, lineHeight: 1,
                      background: 'var(--surfaceHover, #f3f4f6)',
                      border: '1px solid var(--border, #e5e7eb)',
                      borderRadius: 10, padding: '1px 5px',
                    }}
                  >
                    {emoji}<span style={{ color: 'var(--textSecondary, #6b7280)', fontSize: 10 }}>{count}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Overlays live on <body>: the content column is its own stacking context and
          would otherwise draw them below the sidebars (see BodyPortal). Mounted only while one
          is open, so a library of many tiles doesn't keep a full-screen layer per tile. */}
      {hasOpenOverlay && (
      <BodyPortal>
      {/* Video player — no host overlay (ACV's navbar/chat bar stay usable). The content area
          behind it is dimmed so the video is the focus; click outside or Esc closes it. */}
      {showVideoModal && (
        <div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 1050, background: 'rgba(0, 0, 0, 0.6)', overscrollBehavior: 'contain' }}
          onClick={closeVideoModal}
        >
          {/* Video + comments side by side on wide screens; stacked (and scrollable) when narrow. */}
          <div
            className="airec-player relative w-full mx-4 rounded-lg"
            // Sized to the content area (BodyPortal region), not the whole window.
            style={{ maxHeight: 'calc(100% - 32px)', boxShadow: '0 12px 40px rgba(0,0,0,0.28)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeVideoModal}
              className="absolute z-10"
              style={{
                top: 8,
                right: 8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                border: '1px solid var(--border, #e5e7eb)',
                background: 'var(--card, #ffffff)',
                color: 'var(--cardForeground, #1a1a1a)',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.18)',
              }}
              aria-label={t('close')}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div
              className="airec-player__main"
              // Card colour, not black: next to a long comments column this column is stretched
              // to the same height, and the space under the details must not show as a black block.
              style={{ backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))' }}
            >
              {/* Player wrapper — position:relative so the end-of-video overlay stacks on top */}
              <div style={{ position: 'relative', backgroundColor: '#000000' }}>
                <CustomVideoPlayer
                  src={normalizedVideo.streamUrl || normalizedVideo.fileUrl}
                  poster={normalizedVideo.thumbnailUrl || undefined}
                  mimeType={normalizedVideo.mimeType || 'video/webm'}
                  knownDuration={normalizedVideo.duration}
                  videoId={normalizedVideo.id}
                  className="w-full h-auto max-h-[80vh]"
                  onEnded={() => setLibraryVideoEnded(true)}
                  onPlay={() => setLibraryVideoEnded(false)}
                  onSourceError={broadcastVideosRefresh}
                  trackViews={!isVideoOwner}
                  videoElementRef={playerRef}
                />

                {/* End-of-video overlay — replay + optional share link */}
                {libraryVideoEnded && (
                  <div style={{
                    position: 'absolute', inset: 0,
                    background: 'rgba(10,10,18,0.80)',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 20,
                    backdropFilter: 'blur(4px)',
                  }}>
                    <p style={{ color: '#fff', fontSize: 15, fontWeight: 600, margin: 0 }}>
                      {t('video_player.video_ended', 'Video finalizado')}
                    </p>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <button
                        onClick={() => setLibraryVideoEnded(false)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '10px 22px', borderRadius: 6, border: 'none',
                          background: 'rgba(255,255,255,0.15)', color: '#fff',
                          fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        }}
                      >
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                          <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M3 3v5h5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {t('video_player.replay', 'Reproducir de nuevo')}
                      </button>
                      {normalizedVideo.isShared && normalizedVideo.shareToken && (
                        <a
                          href={`/share/${normalizedVideo.shareToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '10px 22px', borderRadius: 6, textDecoration: 'none',
                            background: 'var(--primary, #d33139)', color: 'var(--button-primary-foreground, #fff)',
                            fontSize: 14, fontWeight: 600,
                          }}
                        >
                          <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" strokeLinecap="round" strokeLinejoin="round"/>
                            <polyline points="15 3 21 3 21 9" strokeLinecap="round" strokeLinejoin="round"/>
                            <line x1="10" y1="14" x2="21" y2="3" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          {t('video_player.view_shared', 'Ver página compartida')}
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Video metadata below the player */}
              <div
                className="p-4"
                style={{
                  backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
                  borderTop: '1px solid var(--border-color, var(--theme-border, #d6d6d6))',
                }}
              >
                <div className="flex items-start gap-2">
                  <h3
                    className="text-lg font-semibold line-clamp-2 flex-1 min-w-0 break-words"
                    style={{ color: 'var(--card-title-color, var(--text-primary, var(--theme-foreground, #1a1a1a)))' }}
                  >
                    {normalizedVideo.title}
                  </h3>
                  {isVideoOwner && !readOnly && (
                    <div className="flex flex-shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowDetailsDialog(true)}
                      title={t('video_details.edit_menu', 'Edit details')}
                      aria-label={t('video_details.edit_menu', 'Edit details')}
                      className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-xs"
                      style={{
                        border: '1px solid var(--border-color, var(--theme-border, #d6d6d6))',
                        background: 'transparent',
                        color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
                        cursor: 'pointer',
                      }}
                    >
                      <Pencil className="w-3.5 h-3.5" aria-hidden />
                      {t('video_details.edit_short', 'Edit')}
                    </button>
                    <button
                      type="button"
                      onClick={handleOpenShareModal}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold"
                      style={{
                        border: 'none',
                        background: 'var(--theme-primary, #4f46e5)',
                        color: 'var(--button-primary-foreground, #fff)',
                        cursor: 'pointer',
                      }}
                    >
                      <Share2 className="w-3.5 h-3.5" aria-hidden />
                      {t('video_actions.share')}
                    </button>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  {[
                    formatDate(normalizedVideo.createdAt),
                    t('library_card.views', { count: Number(normalizedVideo.views) || 0 }),
                    formatFileSize(normalizedVideo.fileSize),
                    format,
                    shareStatusLabel,
                  ].filter(Boolean).join(' · ')}
                </p>

                {/* Engagement — only what there is */}
                {(commentCount > 0 || likes > 0 || dislikes > 0 || emojiSummary.length > 0) && (
                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm" style={{ color: 'var(--text-secondary, #6b7280)' }}>
                  {commentCount > 0 && (
                    <span className="flex items-center gap-1" title={t('comments.heading', 'Comments')}>
                      <MessageCircle className="w-4 h-4" aria-hidden /> {commentCount}
                    </span>
                  )}
                  {likes > 0 && (
                    <span className="flex items-center gap-1" title={t('library_card.likes', 'Likes')}>
                      <ThumbsUp className="w-4 h-4" aria-hidden /> {likes}
                    </span>
                  )}
                  {dislikes > 0 && (
                    <span className="flex items-center gap-1" title={t('library_card.dislikes', 'Dislikes')}>
                      <ThumbsDown className="w-4 h-4" aria-hidden /> {dislikes}
                    </span>
                  )}
                  {emojiSummary.length > 0 && (
                    <span className="flex flex-wrap items-center gap-1">
                      {emojiSummary.map(({ emoji, count }) => (
                        <span
                          key={emoji}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontSize: 12, lineHeight: 1,
                            background: 'var(--surfaceHover, #f3f4f6)',
                            border: '1px solid var(--border, #e5e7eb)',
                            borderRadius: 10, padding: '2px 6px',
                          }}
                        >
                          {emoji}<span style={{ opacity: 0.7, fontSize: 11 }}>{count}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </div>
                )}

                {/* Full description (the tile only has room for a preview) */}
                {normalizedVideo.description && (
                  <RichText
                    value={normalizedVideo.description}
                    className="text-sm mt-3"
                    style={{ color: 'var(--text-primary, var(--theme-foreground, #111827))', opacity: 0.86 }}
                  />
                )}
              </div>
            </div>

            {/* Comments — same thread as the share page (comments are keyed by video id). */}
            <aside
              className="airec-player__comments"
              style={{
                backgroundColor: 'var(--card-bg, var(--bg-primary, #ffffff))',
                color: 'var(--text-primary, var(--theme-foreground, #1a1a1a))',
              }}
            >
              {/* Scroll box: side by side it's absolutely positioned, so the comments panel
                  takes the video column's height instead of stretching it (see index.css). */}
              <div className="airec-player__comments-scroll">
              <h4 className="text-sm font-semibold mb-3 pr-10">
                {t('comments.heading', 'Comments')}
                {commentCount > 0 && <span style={{ opacity: 0.6, fontWeight: 400 }}> · {commentCount}</span>}
              </h4>
              <Comments
                videoId={normalizedVideo.id}
                playerRef={playerRef}
                isOwner={isVideoOwner}
                onCommentAdded={() => setCommentCount((n) => n + 1)}
                onCommentDeleted={() => setCommentCount((n) => Math.max(0, n - 1))}
              />
              </div>
            </aside>
          </div>
        </div>
      )}

      {/* ── Delete confirmation (stays open with a spinner until the delete finishes) ── */}
      {showDeleteConfirm && (
        <ConfirmDialog
          danger
          title={t('video_delete.title', 'Move to Recycle bin?')}
          message={
            <>
              {t('video_delete.to_bin', '"{{title}}" moves to the Recycle bin. You can restore it within {{days}} days.', {
                title: normalizedVideo.title,
                days: BIN_RETENTION_DAYS,
                // React escapes the text; i18next escaping would show &amp; etc. in titles.
                interpolation: { escapeValue: false },
              })}
            </>
          }
          confirmLabel={t('video_delete.confirm', 'Move to Recycle bin')}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
        />
      )}

      {/* ── Channel Picker Modal ─────────────────────────────────────────── */}
      {showChannelPicker && (
        <ChannelPickerModal
          channels={resolvedMyChannels}
          currentChannelId={localChannelId}
          videoTitle={video.title}
          assigning={assigningChannel}
          onSelect={(chId) => {
            setShowChannelPicker(false);
            // Picking the channel it's already in is a no-op.
            if (chId !== localChannelId) setChannelConfirm({ kind: 'publish', channelId: chId });
          }}
          onClose={() => setShowChannelPicker(false)}
        />
      )}

      {channelConfirm && (() => {
        const targetId = channelConfirm.kind === 'publish' ? channelConfirm.channelId : localChannelId;
        const channelName = resolvedMyChannels.find((ch) => ch.id === targetId)?.displayName
          || t('channel.this_channel', 'this channel');
        return channelConfirm.kind === 'publish' ? (
          <ConfirmDialog
            title={t('channel.publish_confirm_title', 'Publish to channel?')}
            message={t('channel.publish_confirm_body', {
              title: video.title,
              channel: channelName,
              defaultValue: '"{{title}}" will appear in {{channel}}, and its subscribers will be notified (only once per video).',
            })}
            confirmLabel={t('channel.publish_confirm', 'Publish')}
            onCancel={() => setChannelConfirm(null)}
            onConfirm={async () => {
              await handleAssignChannel(channelConfirm.channelId);
              setChannelConfirm(null);
            }}
          />
        ) : (
          <ConfirmDialog
            danger
            title={t('channel.remove_confirm_title', 'Remove from channel?')}
            message={t('channel.remove_confirm_body', {
              title: video.title,
              channel: channelName,
              defaultValue: '"{{title}}" will no longer appear in {{channel}}. The video stays in your library.',
            })}
            confirmLabel={t('channel.remove_confirm', 'Remove')}
            onCancel={() => setChannelConfirm(null)}
            onConfirm={async () => {
              await handleAssignChannel(null);
              setChannelConfirm(null);
            }}
          />
        );
      })()}
      </BodyPortal>
      )}

      {showShareModal && (
        <ShareLinkDialog
          videoId={normalizedVideo.id}
          // Only a link that works (an unshared video may still carry its old, dead token).
          shareToken={normalizedVideo.isShared && normalizedVideo.shareToken ? normalizedVideo.shareToken : null}
          shareExpiresAt={normalizedVideo.shareExpiresAt}
          shareVisibility={normalizedVideo.shareVisibility as ShareVisibility | undefined}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showMp4Dialog && (
        <Mp4DownloadDialog
          ready={mp4.mp4s}
          isMp4={format === 'MP4'}
          onCancel={() => setShowMp4Dialog(false)}
          onDownload={(preset) => {
            setShowMp4Dialog(false);
            void mp4.start(preset);
          }}
        />
      )}

      {showInsights && (
        <VideoInsightsDialog videoId={video.id} videoTitle={video.title || ''} onClose={() => setShowInsights(false)} />
      )}
      {showDetailsDialog && (
        <EditVideoDetailsDialog
          videoId={video.id}
          title={video.title || ''}
          description={video.description || ''}
          thumbnailUrl={normalizedVideo.thumbnailUrl || undefined}
          videoUrl={video.playbackUrl || normalizedVideo.streamUrl || normalizedVideo.fileUrl}
          duration={normalizedVideo.duration}
          onClose={() => setShowDetailsDialog(false)}
          onSaved={(details) => {
            setEditedDetails(details);
            setShowDetailsDialog(false);
            broadcastVideosRefresh();
          }}
        />
      )}
    </>
  );
};

export default VideoCard;